import TYPES                      from "@/core/services/Changelogs/types";
import Title_ru_RU                from "@/core/misc/decorators/Title_ru_RU";
import AdditionalFields, {field}  from "@/core/misc/decorators/AdditionalFields";
import Title_ru_RU_Class          from "@/core/misc/decorators/Title_ru_RU_Class";
import BaseEntity                 from '@/core/misc/BaseEntity';
import Core 					  from '@/core/Core';
import CharacteristicGroup        from '@/domains/CharacteristicGroups/CharacteristicGroup';
import CharacteristicAllowedValue from '@/domains/Characteristics/CharacteristicAllowedValue';
import CharacteristicUnit         from '@/domains/CharacteristicUnits/CharacteristicUnit';

@AdditionalFields({
  sortField: field("Поле сортировки", TYPES.NUMBER)
})
/**
 * Рамка характеристики — определение поля допустимое для категории продукта.
 *
 * Иерархия:
 *   - Category          — какие рамки вообще можно задать (рамка рамки)
 *   - Characteristic    — сама рамка
 *   - Product           — конкретизация рамки на уровне товара (ProductCharacteristic)
 *   - Offer             — конкретное значение под рамку продукта (OfferCharacteristic)
 */
@Title_ru_RU_Class('Характеристика')                // characteristic → Characteristic
export default class Characteristic extends BaseEntity {
  static Events = {
    Updated: 'characteristic:updated',
  } as const;

  // Режимы фильтрации для selectableMulti
  // при фильтрации характеристик достаточно совпадения хотя бы одной
  static readonly SELECTABLE_LIST_FILTER_MODE_INTERSECTION = 'intersection';
  // при фильтрации характеристик нас интересует, что бы каждое из значений,
  // присутствующие в productCharacteristics присутствовало в offerCharacteristic
  static readonly SELECTABLE_LIST_FILTER_MODE_CONTAINMENT  = 'containment';

  // Типы значений характеристики
  static readonly VALUE_TYPE_STRING            = 'string';
  static readonly VALUE_TYPE_LINK              = 'link';
  static readonly VALUE_TYPE_NUMERIC           = 'numeric';
  static readonly VALUE_TYPE_SELECTABLE_SINGLE = 'selectableSingle';
  static readonly VALUE_TYPE_SELECTABLE_MULTI  = 'selectableMulti';
  static readonly VALUE_TYPE_ENTITY_LINK       = 'entityLink';
  static readonly VALUE_TYPE_IMAGE             = 'image';

  /** Список типов значений для UI (для селекта в карточке). */
  static readonly valueTypeList = [
    { id: Characteristic.VALUE_TYPE_STRING,            name: 'Строка' },
    { id: Characteristic.VALUE_TYPE_LINK,              name: 'Ссылка' },
    { id: Characteristic.VALUE_TYPE_NUMERIC,           name: 'Число' },
    { id: Characteristic.VALUE_TYPE_SELECTABLE_SINGLE, name: 'Одно значение из списка' },
    { id: Characteristic.VALUE_TYPE_SELECTABLE_MULTI,  name: 'Несколько значений из списка' },
    { id: Characteristic.VALUE_TYPE_ENTITY_LINK,       name: 'Ссылка на продукт' },
    { id: Characteristic.VALUE_TYPE_IMAGE,             name: 'Картинка' },
  ] as const;

  /** Список режимов фильтрации для selectableMulti. */
  static readonly selectableListFilterModeList = [
    { id: Characteristic.SELECTABLE_LIST_FILTER_MODE_INTERSECTION, name: 'Пересечение (хотя бы одно совпадает)' },
    { id: Characteristic.SELECTABLE_LIST_FILTER_MODE_CONTAINMENT,  name: 'Вхождение (все значения товара должны быть у оффера)' },
  ] as const;

  @Title_ru_RU('Название', TYPES.STRING)
  name: string;

  @Title_ru_RU('Описание', TYPES.STRING)
  description: string;

  valueType: string;
  unit: CharacteristicUnit | null;
  allowedValues: CharacteristicAllowedValue[] = [];
  validations: any;

  /** Режим фильтрации для типов selectableSingle/selectableMulti (SELECTABLE_LIST_FILTER_MODE_*) */
  selectableListFilterMode: string;

  group: CharacteristicGroup | null;

  constructor() {
    super();

    // я не знаю почему, так было в старом коде
    this.validations = {
      isRequired: true
    }
  }

  static fromGroup(group: CharacteristicGroup): Characteristic {
    const result = new this();

    result.group = group;

    return result;
  }

  static fromServer(data: any, characteristic = new Characteristic()): Characteristic {
    super.fromServer(data, characteristic);

    characteristic.unit          = Core.utils.hydrate(data.unit, CharacteristicUnit.fromServer);
    characteristic.group         = Core.utils.hydrate(data.group, CharacteristicGroup.fromServer);
    characteristic.allowedValues = Core.utils.hydrateArray(data.allowedValues, CharacteristicAllowedValue.fromServer);

    return characteristic;
  }

  getLabel(): string {
    let result = this.name;
    if (this.unit?.name && this.unit?.name !== 'строка') {
      result += `, ${this.unit.name}`;
    }
    return result;
  }

  /**
   * Читаемое имя типа значения характеристики (например: "Число", "Строка",
   * "Картинка"). Если тип неизвестный — возвращает null. Это безопасный
   * фолбэк на случай, когда бэк вернул новый тип, а фронт ещё не обновлён.
   */
  get valueTypeName(): string | null {
    const item = Characteristic.valueTypeList.find(item => item.id === this.valueType);
    if (item === undefined) return null;
    return item.name;
  }

  /**
   * Валидация минимального значения (для valueType = numeric).
   * Минимум не может быть больше максимума.
   */
  validateMin(): string | null {
    const min = parseFloat(String(this.validations?.min ?? ''));
    const max = parseFloat(String(this.validations?.max ?? ''));

    if (isNaN(min) || isNaN(max)) return null;
    if (min > max) return 'Минимальное значение не может быть больше максимального';
    return null;
  }

  /**
   * Валидация максимального значения (для valueType = numeric).
   * Максимум не может быть меньше минимума.
   */
  validateMax(): string | null {
    const min = parseFloat(String(this.validations?.min ?? ''));
    const max = parseFloat(String(this.validations?.max ?? ''));

    if (isNaN(min) || isNaN(max)) return null;
    if (max < min) return 'Максимальное значение не может быть меньше минимального';
    return null;
  }

  /**
   * Валидация минимальной длины (для valueType = string).
   * Не отрицательна и не больше максимальной.
   */
  validateMinLength(): string | null {
    const minLength = parseInt(String(this.validations?.minLength ?? ''), 10);
    const maxLength = parseInt(String(this.validations?.maxLength ?? ''), 10);

    if (!isNaN(minLength) && minLength < 0) return 'Минимальная длина не может быть отрицательной';
    if (isNaN(minLength) || isNaN(maxLength)) return null;
    if (minLength > maxLength) return 'Минимальная длина не может быть больше максимальной';
    return null;
  }

  /**
   * Валидация максимальной длины (для valueType = string).
   * Не отрицательна и не меньше минимальной.
   */
  validateMaxLength(): string | null {
    const minLength = parseInt(String(this.validations?.minLength ?? ''), 10);
    const maxLength = parseInt(String(this.validations?.maxLength ?? ''), 10);

    if (!isNaN(maxLength) && maxLength < 0) return 'Максимальная длина не может быть отрицательной';
    if (isNaN(minLength) || isNaN(maxLength)) return null;
    if (maxLength < minLength) return 'Максимальная длина не может быть меньше минимальной';
    return null;
  }

  get isImage() { return this.valueType === Characteristic.VALUE_TYPE_IMAGE; }
  get isEntityLink() { return this.valueType === Characteristic.VALUE_TYPE_ENTITY_LINK; }
  get isString() { return this.valueType === Characteristic.VALUE_TYPE_STRING; }
  get isLink() { return this.valueType === Characteristic.VALUE_TYPE_LINK; }
  get isNumeric() { return this.valueType === Characteristic.VALUE_TYPE_NUMERIC; }

  get isSelectableSingle() { return this.valueType === Characteristic.VALUE_TYPE_SELECTABLE_SINGLE; }
  get isSelectableMulti() {  return this.valueType === Characteristic.VALUE_TYPE_SELECTABLE_MULTI; }

  /**
   * Характеристика не редактируется продавцом: картинку и ссылку на продукт
   * прикрепляет покупатель на уровне товара, поставщику в офере заполнять
   * их нечем.
   */
  get isReadOnly(): boolean {
    return this.isImage || this.isEntityLink;
  }
}
